import React, { useContext, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FaTrashAlt } from "react-icons/fa";
import * as Yup from "yup";
import "./Work.scss";
import axios from "axios";
import MyContext from "../Context/MyContext";

// Daily work validation schema (outside component)
const dailyWorkSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  work: Yup.string()
    .required("Daily work is required")
    .min(5, "Must be at least 5 characters"),
  date: Yup.date()
    .required("Date is required")
    .nullable()
    .typeError("Invalid date format"),
});

const initialValues = {
  email: "",
  work: "",
  date: "",
};

const Work = () => {
  const { setLoader, user, fetchuser, setOpen, setMessage, url } =
    useContext(MyContext);

  const [screenshots, setScreenshots] = useState({});
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loader, setFormLoader] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchuser();
  }, []);

  useEffect(() => {
    if (user.length > 0) {
      setFilteredUsers(user);
      fetchScreenshots();
    }
  }, [user]);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredUsers(user);
    } else {
      const filtered = user.filter(
        (student) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, user]);

  const handleCardClick = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleDeleteStudent = async (email) => {
    if (deleteConfirm !== email) {
      setDeleteConfirm(email);
      return;
    }

    try {
      setLoader(true);
      const { data } = await axios.delete(`${url}/api/delete-student/${email}`);
      setMessage(data.message);
      setOpen(true);
      fetchuser();
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting student", error);
      setMessage("Failed to delete student");
      setOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleDailyWorkSubmit = async (values, { resetForm }) => {
    setFormLoader(true);
    document.body.style.overflow = "hidden";
    try {
      const { data } = await axios.post(`${url}/daily`, values);
      if (data.success) {
        setMessage(data.message);
        setOpen(true);
        resetForm();
        handleCloseModal();
      } else {
        setMessage(data.error);
        setOpen(true);
      }
    } catch (error) {
      console.error("Error during daily work submission", error);
      alert("Daily work submission failed. Please try again.");
    } finally {
      setFormLoader(false);
      document.body.style.overflow = "auto";
    }
  };

  const handleCertificateAction = async (email, action) => {
    try {
      let duration = "";

      if (action === "approve") {
        duration = prompt("Enter internship duration (e.g., 4 months):");
        if (!duration) return;
      }

      const res = await axios.patch(`${url}/api/approve-certificate/${email}`, {
        status: action === "approve" ? "approved" : "declined",
        duration,
      });

      setMessage(res.data.message);
      setOpen(true);
    } catch (error) {
      console.error("Approval failed", error);
    }
  };

  const fetchScreenshots = async () => {
    const result = {};
    for (const u of user) {
      try {
        const { data } = await axios.get(
          `${url}/api/get-review-screenshot/${u.email}`
        );
        if (data.success) {
          result[u.email] = `${url}${data.screenshotUrl}`;
        }
      } catch (err) {
        console.log(`No screenshot for ${u.email}`);
      }
    }
    setScreenshots(result);
  };

  return (
    <div className="work-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h3>Vemox</h3>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-button ${
              activeTab === "dashboard" ? "active" : ""
            }`}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard
          </button>
          <button
            className={`nav-button ${
              activeTab === "screenshots" ? "active" : ""
            }`}
            onClick={() => setActiveTab("screenshots")}
          >
            Review Screenshots
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="work-main">
        {activeTab === "dashboard" ? (
          <>
            <div className="work-container">
              <div className="header-section">
                <h1 className="headl">Daily Work</h1>
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="total-students">
                    Total Students: {filteredUsers.length}
                  </span>
                </div>
              </div>
              <div className="student-card-container">
                {filteredUsers.map((student) => (
                  <div
                    key={student._id}
                    className="student-card"
                    onClick={() => handleCardClick(student)}
                  >
                    <div className="card-content">
                      <h3>{student.name}</h3>
                      <p>{student.email}</p>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStudent(student.email);
                      }}
                      title="Delete student"
                    >
                      {deleteConfirm === student.email ? (
                        "Confirm?"
                      ) : (
                        <FaTrashAlt className="icon" />
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Modal */}
              {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                  <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <h2>Assign Work to {selectedStudent?.name}</h2>
                    <Formik
                      initialValues={{
                        ...initialValues,
                        email: selectedStudent?.email || "",
                      }}
                      validationSchema={dailyWorkSchema}
                      onSubmit={handleDailyWorkSubmit}
                      enableReinitialize
                    >
                      {() => (
                        <Form className="form">
                          <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <Field
                              type="email"
                              name="email"
                              className="form-input"
                              disabled
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="form-error"
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="work">Work</label>
                            <Field
                              as="textarea"
                              name="work"
                              className="form-input"
                              placeholder="Enter work"
                            />
                            <ErrorMessage
                              name="work"
                              component="div"
                              className="form-error"
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="date">Date</label>
                            <Field
                              type="date"
                              name="date"
                              className="form-input"
                            />
                            <ErrorMessage
                              name="date"
                              component="div"
                              className="form-error"
                            />
                          </div>

                          <div className="form-buttons">
                            <button
                              type="submit"
                              className="btn"
                              disabled={loader}
                            >
                              {loader ? "Submitting..." : "Submit"}
                            </button>
                            <button
                              type="button"
                              className="btn cancel"
                              onClick={handleCloseModal}
                            >
                              Cancel
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="screenshots-container">
            <div className="header-section">
              <h1 className="headl">Review Screenshots</h1>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="total-students">
                  Total Students: {filteredUsers.length}
                </span>
              </div>
            </div>
            <div className="screenshots-grid">
              {filteredUsers.map(
                (i) =>
                  screenshots[i.email] && (
                    <div key={i._id} className="screenshot-card">
                      <img src={screenshots[i.email]} alt="Review Screenshot" />
                      <div className="card-footer">
                        <p>{i.name}</p>
                        <div className="action-buttons">
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteStudent(i.email)}
                            title="Delete student"
                          >
                            {deleteConfirm === i.email ? (
                              "Confirm?"
                            ) : (
                              <FaTrashAlt className="icon" />
                            )}
                          </button>
                          <button
                            title="Approve Certificate"
                            className="approve-btn"
                            onClick={() =>
                              handleCertificateAction(i.email, "approve")
                            }
                          >
                            ✅
                          </button>
                          <button
                            title="Decline Certificate"
                            className="decline-btn"
                            onClick={() =>
                              handleCertificateAction(i.email, "decline")
                            }
                          >
                            ❌
                          </button>
                        </div>
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Work;
